import { useCallback, useEffect, useMemo, useState } from 'react';
import { User, AuthPayload } from '../types';
import * as authService from '../services/auth.service';
import { clearAuthStorage, loadInitialAuth, persistAuth, isTokenExpired } from '../utils/auth.utils';
import { editorRole } from '../constants/roles';

export function useAuth() {
  const initialAuth = useMemo(() => loadInitialAuth(), []);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [accessToken, setAccessToken] = useState<string | undefined>(initialAuth.accessToken);
  const [refreshToken, setRefreshToken] = useState<string | undefined>(initialAuth.refreshToken);
  const [user, setUser] = useState<User | undefined>(initialAuth.user);
  const [refreshing, setRefreshing] = useState(false);

  // Register user state
  const [registerName, setRegisterName] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerRoles, setRegisterRoles] = useState<string[]>([editorRole]);
  const [registerRolePopoverOpen, setRegisterRolePopoverOpen] = useState(false);
  const [registerRoleSearch, setRegisterRoleSearch] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | undefined>();
  const [registerSuccess, setRegisterSuccess] = useState<string | undefined>();

  const isAuthenticated = !!accessToken && !!user;

  // Token expiration check - silently logout when token expires
  useEffect(() => {
    if (!accessToken || !isAuthenticated) return;

    // Check immediately on mount
    if (isTokenExpired(accessToken)) {
      clearAuthStorage();
      setAccessToken(undefined);
      setRefreshToken(undefined);
      setUser(undefined);
      return;
    }

    // Check every minute for token expiration
    const interval = setInterval(() => {
      if (isTokenExpired(accessToken)) {
        clearAuthStorage();
        setAccessToken(undefined);
        setRefreshToken(undefined);
        setUser(undefined);
      }
    }, 60000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [accessToken, isAuthenticated]);

  const attemptRefresh = useCallback(async () => {
    if (!refreshToken) return false;

    setRefreshing(true);

    try {
      const payload = await authService.refreshToken(refreshToken);
      setAccessToken(payload.accessToken);
      setRefreshToken(payload.refreshToken);
      setUser(payload.user);
      persistAuth(payload);
      return true;
    } catch (err) {
      clearAuthStorage();
      setAccessToken(undefined);
      setRefreshToken(undefined);
      setUser(undefined);
      return false;
    } finally {
      setRefreshing(false);
    }
  }, [refreshToken]);

  const login = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    try {
      const payload = await authService.login({ username, password });
      setAccessToken(payload.accessToken);
      setRefreshToken(payload.refreshToken);
      setUser(payload.user);
      persistAuth(payload);
      setPassword('');
      setUsername('');
      return { success: true, message: `Bem-vindo(a), ${payload.user.name}!` };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Erro ao autenticar'
      };
    }
  }, [username, password]);

  const logout = useCallback(async (): Promise<string> => {
    if (!refreshToken) {
      clearAuthStorage();
      setAccessToken(undefined);
      setRefreshToken(undefined);
      setUser(undefined);
      return 'Sessão encerrada com sucesso.';
    }

    try {
      await authService.logout({ accessToken, refreshToken });
    } finally {
      clearAuthStorage();
      setAccessToken(undefined);
      setRefreshToken(undefined);
      setUser(undefined);
    }

    return 'Sessão encerrada com sucesso.';
  }, [accessToken, refreshToken]);

  const register = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    if (!accessToken) return { success: false, message: 'Não autenticado' };

    setRegisterLoading(true);
    setRegisterError(undefined);
    setRegisterSuccess(undefined);

    try {
      await authService.register(
        {
          name: registerName,
          username: registerUsername,
          email: registerEmail,
          password: registerPassword,
          roles: registerRoles
        },
        accessToken
      );

      setRegisterSuccess('Usuário criado com sucesso.');
      const userName = registerName;

      // Clear form
      setRegisterName('');
      setRegisterUsername('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterRoles([editorRole]);

      return { success: true, message: `Usuário ${userName} cadastrado.` };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro inesperado ao registrar usuário';
      setRegisterError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setRegisterLoading(false);
    }
  }, [accessToken, registerEmail, registerName, registerPassword, registerRoles, registerUsername]);

  const toggleRole = useCallback((roleId: string) => {
    setRegisterRoles((current) => {
      const newRoles = current.includes(roleId)
        ? current.filter((role) => role !== roleId)
        : [...current, roleId];
      return newRoles;
    });
  }, []);

  const removeRole = useCallback((roleId: string) => {
    setRegisterRoles((current) => current.filter((role) => role !== roleId));
  }, []);

  return {
    // Auth state
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    refreshing,

    // Login state
    username,
    setUsername,
    password,
    setPassword,

    // Register state
    registerName,
    setRegisterName,
    registerUsername,
    setRegisterUsername,
    registerEmail,
    setRegisterEmail,
    registerPassword,
    setRegisterPassword,
    registerRoles,
    setRegisterRoles,
    registerRolePopoverOpen,
    setRegisterRolePopoverOpen,
    registerRoleSearch,
    setRegisterRoleSearch,
    registerLoading,
    registerError,
    registerSuccess,

    // Auth actions
    login,
    logout,
    attemptRefresh,
    register,
    toggleRole,
    removeRole
  };
}
